/**
 * The deck design system: every colour role a deck draws, derived from one
 * brand input.
 *
 * - The default theme is `deriveDeckTheme` on the Fairgen primary. tokens.css
 *   carries it as a generated block, and `deck-theme.json` (slide-schemas)
 *   carries it for the PowerPoint export (`scripts/check-generated.sh`).
 * - A client brand is the same function on the client's primary and chart
 *   accent (`applyThemeBrandTokens`, deck scope). Spacing, panels and the type
 *   scale stay the default's; only the hue moves.
 *
 * Pure: colour arithmetic and the computable palette checks only.
 */

/** Fairgen's brand primary (#0EBFA5). The app shell default and the deck seed. */
export const FAIRGEN_PRIMARY_HSL = '171 86% 40%'
/** Fairgen's soft accent (card seed), light and dark: the brand apply's default for a brand without one. */
export const FAIRGEN_SOFT_ACCENT_HSL = '154 85% 84%'
export const FAIRGEN_SOFT_ACCENT_DARK_HSL = '167 37% 10%'

/**
 * The deck's default faces (fairgen.ai): headlines and body. A client brand's
 * heading and body fonts replace them through --font-display / --font-geist-sans.
 */
export const DECK_FONTS = {
  heading: '"Sumana", Georgia, "Times New Roman", serif',
  body: '"Geist", system-ui, -apple-system, sans-serif',
} as const

export type Rgb = { r: number; g: number; b: number }

export interface DeckTheme {
  /** Headlines and strong text: near-black tinted with the brand hue. */
  textPrimary: string
  /** Body copy. */
  textSecondary: string
  /** Labels, captions, axis text. */
  textMuted: string
  /** Eyebrows, bullets, KPI figures: the brand hue at text contrast. */
  accent: string
  /** Light brand wash behind highlight cards. */
  accentSoft: string
  /** Plot and card panels. */
  panel: string
  /** Rules and dividers. */
  rule: string
  /** Tracks and empty bar backgrounds. */
  neutralLight: string
  /** Categorical chart slots 1–8; slot 8 is the neutral "other" slot. */
  chart: Array<string>
}

/** The CSS custom property each role writes. One table for tokens.css and the brand apply. */
export const DECK_THEME_TOKENS: Readonly<Record<Exclude<keyof DeckTheme, 'chart'>, string>> = {
  textPrimary: '--slide-text-primary',
  textSecondary: '--slide-text-secondary',
  textMuted: '--slide-text-muted',
  accent: '--slide-accent',
  accentSoft: '--slide-accent-soft',
  panel: '--slide-card-bg',
  rule: '--slide-border-soft',
  neutralLight: '--slide-neutral-light',
}

export const DECK_CHART_SLOT_COUNT = 8

export function deckThemeCssVars(theme: DeckTheme): Record<string, string> {
  const vars: Record<string, string> = {}
  for (const [role, token] of Object.entries(DECK_THEME_TOKENS)) {
    vars[token] = theme[role as keyof typeof DECK_THEME_TOKENS]
  }
  theme.chart.forEach((color, index) => {
    vars[`--slide-chart-${index + 1}`] = color
  })
  return vars
}

// ── Colour conversions ──────────────────────────────────────────────────────

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

export function parseColor(value: string | null | undefined): Rgb | null {
  if (!value) return null
  const text = value.trim()
  const hex = text.match(/^#([0-9a-f]{6})$/i)
  if (hex) {
    const n = parseInt(hex[1], 16)
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
  }
  const hsl = text.match(/^(?:hsl\(\s*)?(-?\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%\s*\)?$/i)
  if (hsl) return hslToRgb(Number(hsl[1]), Number(hsl[2]), Number(hsl[3]))
  return null
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const hue = ((h % 360) + 360) % 360
  const sat = clamp(s, 0, 100) / 100
  const light = clamp(l, 0, 100) / 100
  const k = (n: number) => (n + hue / 30) % 12
  const a = sat * Math.min(light, 1 - light)
  const f = (n: number) => light - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))
  return { r: Math.round(f(0) * 255), g: Math.round(f(8) * 255), b: Math.round(f(4) * 255) }
}

function rgbToHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l: l * 100 }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h = max === rn ? (gn - bn) / d + (gn < bn ? 6 : 0) : max === gn ? (bn - rn) / d + 2 : (rn - gn) / d + 4
  h *= 60
  return { h, s: s * 100, l: l * 100 }
}

export function toHex({ r, g, b }: Rgb): string {
  return `#${[r, g, b].map((c) => clamp(Math.round(c), 0, 255).toString(16).padStart(2, '0')).join('')}`
}

const toLinear = (c: number) => {
  const v = c / 255
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
}
const fromLinear = (c: number) => 255 * (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055)

function linear(color: Rgb): [number, number, number] {
  return [toLinear(color.r), toLinear(color.g), toLinear(color.b)]
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const lum = (c: Rgb) => {
    const [r, g, bl] = linear(c)
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl
  }
  const [hi, lo] = [lum(a), lum(b)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}

function oklabFromLinear([r, g, b]: [number, number, number]): [number, number, number] {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

function linearFromOklab([L, a, b]: [number, number, number]): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]
}

export function oklch(color: Rgb): { L: number; C: number; h: number } {
  const [L, a, b] = oklabFromLinear(linear(color))
  return { L, C: Math.hypot(a, b), h: ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360 }
}

/** OKLCH → sRGB, lowering chroma until the colour is inside the sRGB gamut. */
export function fromOklch(L: number, C: number, h: number): Rgb {
  const rad = (h * Math.PI) / 180
  let chroma = C
  for (let step = 0; step < 60; step += 1) {
    const lin = linearFromOklab([L, chroma * Math.cos(rad), chroma * Math.sin(rad)])
    if (lin.every((c) => c >= -1e-4 && c <= 1 + 1e-4)) {
      const [r, g, b] = lin.map((c) => fromLinear(clamp(c, 0, 1)))
      return { r: Math.round(r), g: Math.round(g), b: Math.round(b) }
    }
    chroma *= 0.97
  }
  const [r, g, b] = linearFromOklab([L, 0, 0]).map((c) => fromLinear(clamp(c, 0, 1)))
  return { r: Math.round(r), g: Math.round(g), b: Math.round(b) }
}

// ── Palette checks (dataviz validate_palette thresholds) ───────────────────

/** Machado, Oliveira & Fernandes (2009) CVD transforms at severity 1.0 (linear RGB). */
const MACHADO = {
  protan: [
    [0.152286, 1.052583, -0.204868],
    [0.114503, 0.786281, 0.099216],
    [-0.003882, -0.048116, 1.051998],
  ],
  deutan: [
    [0.367322, 0.860646, -0.227968],
    [0.280085, 0.672501, 0.047413],
    [-0.01182, 0.04294, 0.968881],
  ],
} as const

function simulate(color: Rgb, kind: keyof typeof MACHADO): [number, number, number] {
  const [r, g, b] = linear(color)
  const M = MACHADO[kind]
  return M.map((row) => clamp(row[0] * r + row[1] * g + row[2] * b, 0, 1)) as [number, number, number]
}

/** OKLab ΔE ×100: unsimulated, or the worse of protan and deutan. */
export function deltaE(a: Rgb, b: Rgb, vision: 'normal' | 'cvd'): number {
  const distance = (x: [number, number, number], y: [number, number, number]) => {
    const p = oklabFromLinear(x)
    const q = oklabFromLinear(y)
    return 100 * Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2])
  }
  if (vision === 'normal') return distance(linear(a), linear(b))
  return Math.min(distance(simulate(a, 'protan'), simulate(b, 'protan')), distance(simulate(a, 'deutan'), simulate(b, 'deutan')))
}

export const PALETTE_CHECK = {
  lightnessBand: [0.43, 0.77] as const,
  chromaFloor: 0.1,
  markContrast: 3,
  adjacentCvd: 8,
  normalFloor: 15,
  /** Any two slots, adjacent or not: never a near-repeat. */
  distinctFloor: 8,
} as const

/** A mark colour's own checks: lightness band, chroma floor, 3:1 on every surface. */
export function markPasses(color: Rgb, surfaces: ReadonlyArray<Rgb>): boolean {
  const { L, C } = oklch(color)
  const [lo, hi] = PALETTE_CHECK.lightnessBand
  return (
    L >= lo &&
    L <= hi &&
    C >= PALETTE_CHECK.chromaFloor &&
    surfaces.every((surface) => contrastRatio(color, surface) >= PALETTE_CHECK.markContrast)
  )
}

export interface PaletteReport {
  ok: boolean
  worstAdjacentCvd: number
  worstNormal: number
  worstDistinct: number
  minContrast: number
  failures: Array<string>
}

/** The categorical checks over slots 1..n (the neutral slot is not categorical). */
export function checkCategoricalPalette(colors: ReadonlyArray<string>, surfaces: ReadonlyArray<string>): PaletteReport {
  const marks = colors.map((c) => parseColor(c) as Rgb)
  const grounds = surfaces.map((c) => parseColor(c) as Rgb)
  const failures: Array<string> = []
  marks.forEach((mark, index) => {
    if (!markPasses(mark, grounds)) failures.push(`slot ${index + 1} ${colors[index]} fails band, chroma or 3:1`)
  })
  let worstAdjacentCvd = Infinity
  let worstNormal = Infinity
  let worstDistinct = Infinity
  for (let i = 0; i < marks.length; i += 1) {
    for (let j = i + 1; j < marks.length; j += 1) {
      const normal = deltaE(marks[i], marks[j], 'normal')
      worstDistinct = Math.min(worstDistinct, normal)
      if (j === i + 1) {
        worstNormal = Math.min(worstNormal, normal)
        worstAdjacentCvd = Math.min(worstAdjacentCvd, deltaE(marks[i], marks[j], 'cvd'))
      }
    }
  }
  if (worstAdjacentCvd < PALETTE_CHECK.adjacentCvd) failures.push(`adjacent CVD ΔE ${worstAdjacentCvd.toFixed(1)}`)
  if (worstNormal < PALETTE_CHECK.normalFloor) failures.push(`adjacent normal-vision ΔE ${worstNormal.toFixed(1)}`)
  if (worstDistinct < PALETTE_CHECK.distinctFloor) failures.push(`near-repeat ΔE ${worstDistinct.toFixed(1)}`)
  const minContrast = Math.min(...marks.flatMap((mark) => grounds.map((g) => contrastRatio(mark, g))))
  return { ok: failures.length === 0, worstAdjacentCvd, worstNormal, worstDistinct, minContrast, failures }
}

// ── Derivation ─────────────────────────────────────────────────────────────

const WHITE: Rgb = { r: 255, g: 255, b: 255 }

/**
 * The design system's supporting hues: an OKLCH wheel, 20° apart, chroma 0.13,
 * lightness alternating 0.55 / 0.65 (alternating lightness keeps neighbours
 * apart for colour-blind readers). Only the ones that pass as marks on the
 * theme's surfaces are offered.
 */
const SUPPORTING_WHEEL = { hueStep: 20, hueOffset: 15, chroma: 0.13, lightness: [0.55, 0.65] } as const

function supportingHues(surfaces: ReadonlyArray<Rgb>): Array<Rgb> {
  const { hueStep, hueOffset, chroma, lightness } = SUPPORTING_WHEEL
  const hues: Array<Rgb> = []
  for (let index = 0; index * hueStep < 360; index += 1) {
    hues.push(fromOklch(lightness[index % 2], chroma, hueOffset + index * hueStep))
  }
  return hues.filter((color) => markPasses(color, surfaces))
}

function tint(hsl: { h: number; s: number }, maxSaturation: number, factor: number, lightness: number): string {
  return toHex(hslToRgb(hsl.h, Math.min(hsl.s * factor, maxSaturation), lightness))
}

/** The brand hue, darkened until it reads as text (4.5:1) on every surface. */
function textAccent(primary: Rgb, surfaces: ReadonlyArray<Rgb>): string {
  const { h, s, l } = rgbToHsl(primary)
  let lightness = Math.min(l, 50)
  while (lightness > 5 && surfaces.some((surface) => contrastRatio(hslToRgb(h, s, lightness), surface) < 4.5)) {
    lightness -= 0.5
  }
  return toHex(hslToRgb(h, s, lightness))
}

/**
 * The brand hue as a chart mark: the lightness nearest the brand's own (no
 * lighter than the supporting hues) that lands in the band, above the chroma
 * floor, at 3:1 on every surface.
 */
function brandMark(color: Rgb, surfaces: ReadonlyArray<Rgb>): Rgb {
  const { L, C, h } = oklch(color)
  const chroma = Math.max(C, PALETTE_CHECK.chromaFloor + 0.01)
  const [lo, hi] = PALETTE_CHECK.lightnessBand
  const steps: Array<number> = []
  for (let lightness = lo + 0.005; lightness <= hi; lightness += 0.005) steps.push(lightness)
  const target = Math.min(L, SUPPORTING_WHEEL.lightness[1] - 0.03)
  steps.sort((a, b) => Math.abs(a - target) - Math.abs(b - target))
  for (const lightness of steps) {
    const mark = fromOklch(lightness, chroma, h)
    if (markPasses(mark, surfaces)) return mark
  }
  throw new Error(`deck design system: no chart mark passes for ${toHex(color)}`)
}

/**
 * Slots 1–7. Slot 1 is the brand mark. Slots 2–7 are the ordering of supporting
 * hues that keeps every neighbour pair apart (colour-blind and normal vision)
 * and, among those, makes the closest pair of any two slots as far apart as
 * possible. Ties keep wheel order, so the result is stable.
 */
function categoricalSlots(brand: Rgb, surfaces: ReadonlyArray<Rgb>): Array<Rgb> {
  const first = brandMark(brand, surfaces)
  const colors = [first, ...supportingHues(surfaces)]
  const cvd = colors.map((a) => colors.map((b) => deltaE(a, b, 'cvd')))
  const normal = colors.map((a) => colors.map((b) => deltaE(a, b, 'normal')))
  const target = DECK_CHART_SLOT_COUNT - 1
  let best: { order: Array<number>; closest: number } | null = null
  const used = new Set<number>([0])
  const walk = (order: Array<number>, closest: number) => {
    if (best && closest <= best.closest) return
    if (order.length === target) {
      best = { order: [...order], closest }
      return
    }
    const previous = order[order.length - 1]
    for (let next = 1; next < colors.length; next += 1) {
      if (used.has(next)) continue
      if (cvd[previous][next] < PALETTE_CHECK.adjacentCvd || normal[previous][next] < PALETTE_CHECK.normalFloor) continue
      const nearest = Math.min(...order.map((placed) => normal[placed][next]))
      if (nearest < PALETTE_CHECK.distinctFloor) continue
      used.add(next)
      order.push(next)
      walk(order, Math.min(closest, nearest))
      order.pop()
      used.delete(next)
    }
  }
  walk([0], Infinity)
  const found = best as { order: Array<number>; closest: number } | null
  // Unreachable for any brand colour (deck-design-system.test sweeps the colour space).
  if (!found) throw new Error(`deck design system: no categorical palette passes for ${toHex(first)}`)
  return found.order.map((index) => colors[index])
}

export interface DeckThemeSeed {
  /** The brand primary (`H S% L%` or `#rrggbb`). */
  primary: string
  /** The brand's chart colour (its chosen chart palette's primary or its chart accent), when set; slot 1 is drawn from it. */
  chartAccent?: string | null
}

export function deriveDeckTheme(seed: DeckThemeSeed): DeckTheme {
  const primary = parseColor(seed.primary) ?? (parseColor(FAIRGEN_PRIMARY_HSL) as Rgb)
  const chartSeed = parseColor(seed.chartAccent) ?? primary
  const hsl = rgbToHsl(primary)
  const panel = tint(hsl, 8, 0.1, 96.5)
  const accentSoft = tint(hsl, 55, 0.65, 92)
  const textMuted = tint(hsl, 6, 0.08, 40)
  const surfaces = [WHITE, parseColor(panel) as Rgb]
  return {
    textPrimary: tint(hsl, 37, 0.45, 10),
    textSecondary: tint(hsl, 12, 0.15, 23),
    textMuted,
    accent: textAccent(primary, [...surfaces, parseColor(accentSoft) as Rgb]),
    accentSoft,
    panel,
    rule: tint(hsl, 8, 0.1, 89),
    neutralLight: tint(hsl, 10, 0.12, 93),
    chart: [...categoricalSlots(chartSeed, surfaces).map(toHex), tint(hsl, 8, 0.1, 64)],
  }
}

export const DEFAULT_DECK_THEME: DeckTheme = deriveDeckTheme({ primary: FAIRGEN_PRIMARY_HSL })
